import * as React from 'react';
import furoApi from "../api/FuroApi";
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from "../app/ApplicationContext";
import {ErrorResponse, Event, EventType, GridRequest, PageResponseEnvelope} from "../api/dtos/dtos";
import {ErrorAlert} from "../common/ErrorAlert";
import {createEventDto, EventDto} from "../event-details/dtos";
import * as moment from "moment";
import accountProvider from "../account/AccountProvider";
import {Button, Table} from "antd";
import * as tableUtils from "../utils/table-utils";
import {filterableColumn, GridState, searchableColumn, simpleColumn, sortableColumn} from "../utils/table-utils";
import {ColumnProps} from "antd/lib/table";
import locales from "../locales";
import ButtonGroup from "antd/lib/button/button-group";
import ButtonBar from "../common/ButtonBar";
import {getEventTypeFilters, renderEventTypeElement} from "./EventType";
import {DateRangePicker} from "../common/DateRangePicker";
import * as paths from "../paths";
import {IdPageUrlParams} from "../paths";
import {RouteComponentProps, withRouter} from "react-router";
import {MOMENT_DATE_TIME_FORMAT, toApiDateFormat, toEndOfYear, toStartOfYear} from "../utils/date-utils";
import {showConfirm} from "../utils/dialogs";


interface State extends GridState<EventDto> {
  error?: ErrorResponse;
  startFilter?: moment.Moment;
  endFilter?: moment.Moment;
}

const EVENT_START = 'start';
const EVENT_END = 'end';
const USER_NAME = 'salesUserName';
const TYPE_KEY = 'type';

const initialGridState = (): GridState<EventDto> => {
  return {
    ...tableUtils.initialGridState(),
    filters: {
      [TYPE_KEY]: [EventType.PERSONAL_MEETING, EventType.ONLINE_MEETING],
    }
  }
};


class ClientCalendarPage extends React.Component<RouteComponentProps<IdPageUrlParams>, State> {

  public readonly loadingHandler?: LoadingOverlayHandler;

  constructor(props: RouteComponentProps<IdPageUrlParams>) {
    super(props);

    this.state = {
      // Default initial grid state
      ...initialGridState(),

      startFilter: toStartOfYear(moment()),
      endFilter: toEndOfYear(moment()),
      sortOrder: "descend",
      sortColumnKey: EVENT_START,
    }
  }

  public componentDidMount() {
    this.loadEvents();
  }

  public render(): React.ReactNode {
    const {error} = this.state;

    const columns: Array<ColumnProps<EventDto>> = [
      sortableColumn(locales.event_time_range_start, EVENT_START, this.state, (e: EventDto) => e.start.format(MOMENT_DATE_TIME_FORMAT)),
      sortableColumn(locales.event_time_range_end, EVENT_END, this.state, (e: EventDto) => e.end.format(MOMENT_DATE_TIME_FORMAT)),
      simpleColumn(locales.event_subject, (e: EventDto) => e.subject),
    ];

    if (accountProvider.isAdmin()) {
      columns.push(searchableColumn(locales.event_sales_user, USER_NAME, this.state, (e: EventDto) => e.userName));

    } else if (accountProvider.isSales()) {
      columns.push(simpleColumn(locales.event_sales_user, (e: EventDto) => e.userName))
    }

    columns.push(...[
      filterableColumn(locales.type, TYPE_KEY, this.state, (e: EventDto) => renderEventTypeElement(e.type), getEventTypeFilters(), true),
      simpleColumn(locales.client, (e: EventDto) => e.client?.name ?? '-'),
      simpleColumn(locales.contact, (e: EventDto) => e.contact?.name),
      simpleColumn(locales.comment, (e: EventDto) => e.comment ?? '-'),
      {key: 'actions', width: 80, render: this.renderActionColumn,}
    ]);

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <ButtonBar style={{marginBottom: 8}}>
          <DateRangePicker defaultStart={this.state.startFilter} defaultEnd={this.state.endFilter}
                           onRangeChanged={this.onDateRangeFilterChange}/>
        </ButtonBar>

        <Table
          dataSource={this.state.data}
          columns={columns}
          bordered={true}
          size="small"
          rowKey={this.getRowKey}
          pagination={tableUtils.defaultPaginationConfig(this.state)}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
          scroll={{x: 1024}}
        />

        <ErrorAlert style={{marginTop: 16}} error={error}/>
      </>
    );
  }

  private getRowKey = (record: EventDto): string => {
    return tableUtils.idRowKeyFn(record)
  }

  private onGridChange = (newState: GridState<EventDto>) => {
    this.setState({...newState}, () => this.loadEvents());
  };

  private onDateRangeFilterChange = (start?: moment.Moment, end?: moment.Moment) => {
    this.setState({startFilter: start, endFilter: end}, () => this.loadEvents());

  };

  private getIdParam = (): number => {
    const idParam = this.props.match.params.id;
    return Number(idParam);
  };


  private loadEvents() {
    const request = tableUtils.toGridRequest(this.state);
    this.addExtraFiltersToGridRequest(request);

    const clientId = this.getIdParam();
    const promise = furoApi.events.getClientEvents(clientId, request)
      .then(this.onEventsLoaded)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  }

  private onEventsLoaded = (response: PageResponseEnvelope<Event>) => {
    const eventDtos = response.results.content.map((e) => createEventDto(e));
    this.setState({
      ...tableUtils.toGridState({
        results: {
          content: eventDtos,
          total: response.results.total,
          currentPage: response.results.currentPage
        }
      }),
      error: undefined,
    });
  }

  private addExtraFiltersToGridRequest(request: GridRequest) {
    const start = this.state.startFilter;
    const end = this.state.endFilter;

    if (start !== null) {
      request.filters = {
        ...request.filters,
        'start': start ? [toApiDateFormat(start)] : [],
      };
    }
    if (end !== null) {
      request.filters = {
        ...request.filters,
        'end': end ? [toApiDateFormat(end)] : [],
      };
    }
  }

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  }

  private renderActionColumn = (text: any, record: EventDto, index: number) => {
    const onEditEventClick = () => this.onEditEventClick(record);
    const onDeleteEventClick = () => this.onDeleteEventClick(record);
    const editable = accountProvider.isAdmin() || accountProvider.getUserName()! === record.userName;
    return (editable &&
        <div className="action-column" style={{textAlign: 'center'}}>
            <ButtonGroup>
                <Button icon="edit" onClick={onEditEventClick} title={locales.edit}/>
                <Button icon="delete" onClick={onDeleteEventClick} title={locales.edit}/>
            </ButtonGroup>
        </div>
    );
  };

  private onEditEventClick = (event: EventDto) => {
    this.props.history.push(paths.viewEventPath(event.id));
  };

  private onDeleteEventClick = (event: EventDto) => {
    const deleteAlert = () => {
      const promise = furoApi.events.delete(event.id!)
        .then(this.loadEvents)
        .catch(this.onApiError);
      withLoadingOverlay(promise, this.loadingHandler);
    };
    showConfirm(locales.event_delete_confirm, deleteAlert);
  };

}

export default withRouter(ClientCalendarPage);


// WEBPACK FOOTER //
// ./src/client-calendar/ClientCalendarPage.tsx